import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectActors, selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import { IEmotionBubble } from "../../../models/interactions/EmotionBubble";
import "./EmotionBubblePreviewBody.css";
import MessageBubbleActorDropdown from "../message_bubble/MessageBubbleActorDropdown";
import MessageBubbleAnimationDropdown from "../message_bubble/MessageBubbleAnimationDropdown";
import EmotionBubblePreviewContent from "./EmotionBubblePreviewContent";
import ActorPropDropdown from "../lookat/ActorPropDropdown";
import usePropHelper from "../../../hooks/PropHelper";
import { useTranslation } from "react-i18next";
import ActorAvatar from "../../../components/actors/ActorAvatar";
import PropAvatar from "../../../components/actors/PropAvatar";
import PiEmotionGrid from "../components/PiEmotionGrid";
import { Prop } from "../../../models/Prop";
import { emotionBubbleStylesMapped } from "../../../models/interactions/EmotionBubble";
import PiAnimationPicker from "../animation/PiAnimationPicker";
import { noMessageBubbleAnimation } from "../../../models/interactions/MessageBubble";
import PiActorChipSelect from "../components/PiActorChipSelect";
import PiLookModeChipSelect from "../components/PiLookModeChipSelect";
import PiActorPropChipSelect from "../components/PiActorPropChipSelect";
import { PiSettingsGap, PiSettingsList, PiSettingsRow } from "../components/PiSettingsList";
import { hasLookatTarget } from "../../../models/NoLookat";

type EmotionBubblePreviewBodyVariant = "standalone" | "embedded";

export default function EmotionBubblePreviewBody(props: {
    emotionBubble: IEmotionBubble;
    updateInteraction: (field: string, value: any, patch?: Partial<IEmotionBubble>) => void;
    variant?: EmotionBubblePreviewBodyVariant;
}) {

    const { emotionBubble, updateInteraction, variant = "embedded" } = props;
    const take = useSelector(selectSelectedTake3D);
    const { getBubbleTargetProps } = usePropHelper();
    const propsInTake = take
        ? getBubbleTargetProps(take, {
            kind: "timeline",
            interactionId: emotionBubble.id,
        })
        : [];
    const isPropIdInTake = (objectId: string) =>
        propsInTake.some((prop) => prop.id === objectId);
    const isPropAnchor = isPropIdInTake(emotionBubble.npcId);
    const isSelfLookAt =
        !!emotionBubble.lookAt && emotionBubble.lookAt === emotionBubble.npcId;
    const handleAnchorChange = (objectId: string) => {
        // A prop can't look at anything or play an animation.
        if (isPropIdInTake(objectId)) {
            updateInteraction("npcId", objectId, {
                lookAt: "",
                animation: { ...noMessageBubbleAnimation },
            });
            return;
        }
        // An actor can't look at itself.
        if (objectId === emotionBubble.lookAt) {
            updateInteraction("npcId", objectId, { lookAt: "" });
            return;
        }

        updateInteraction("npcId", objectId);
    };

    useEffect(() => {
        if (!emotionBubble.lookAt) return;
        if (!isPropAnchor && !isSelfLookAt) return;
        updateInteraction("lookAt", "");
    }, [emotionBubble.lookAt, isPropAnchor, isSelfLookAt, updateInteraction]);

    if (variant === "embedded") {
        return (
            <div className="emotionbubblepreviewbody-root">
                <div className="emotionbubblepreviewbody-controls">
                    <div className="emotionbubblepreviewbody-actorColumn">
                        <ActorPropDropdown label={"Actor"} selectedObjectId={emotionBubble.npcId} onClick={handleAnchorChange} availableProps={propsInTake} />
                        {isPropAnchor ? (
                            <DisabledLookAtField label="Look At" />
                        ) : (
                            <MessageBubbleActorDropdown label={"Look At"} selectedTargetActorId={emotionBubble.lookAt} onClick={(actorId) => updateInteraction("lookAt", actorId)} excludeActors={[emotionBubble.npcId]} allowNoActor />
                        )}
                    </div>
                    <div className="emotionbubblepreviewbody-animationColumn">
                        <SettingsSection label="Animation">
                            <MessageBubbleAnimationDropdown selectedAnimation={emotionBubble.animation} onClick={(animation) => updateInteraction("animation", animation)} variant="preview" />
                        </SettingsSection>
                    </div>
                </div>

                <div className={"w-full flex-1 min-h-0"}>
                    <EmotionBubblePreviewContent emotionBubble={emotionBubble} onChange={(emotion) => updateInteraction("messageBubbleStyle", emotion)} />
                </div>
            </div>
        );
    }

    // Design: aligned with the Message Bubble popup (Design Studio
    // `screens/lookat-rework/r2-bubble.html`), with Emotion in place of Bubble style.
    return (
        <StandaloneEmotionBubbleBody
            emotionBubble={emotionBubble}
            updateInteraction={updateInteraction}
            propsInTake={propsInTake}
            isPropAnchor={isPropAnchor}
            onAnchorChange={handleAnchorChange}
        />
    );
}

function StandaloneEmotionBubbleBody(props: {
    emotionBubble: IEmotionBubble;
    updateInteraction: (field: string, value: any, patch?: Partial<IEmotionBubble>) => void;
    propsInTake: Prop[];
    isPropAnchor: boolean;
    onAnchorChange: (objectId: string) => void;
}) {
    const { emotionBubble, updateInteraction, propsInTake, isPropAnchor } = props;
    const { t } = useTranslation();

    const currentEmotion = emotionBubbleStylesMapped.find(
        (emotion) => emotion.name === emotionBubble.messageBubbleStyle
    );

    return (
        <div className="w-full h-full min-h-0 grid grid-cols-[minmax(0,1fr)_380px] gap-4">
            {/* Design: Design Studio `screens/bubbles-current/emotion-bubble.html`. Same shape as
                the Message Bubble popup: the preview on the left, every setting on the right,
                with the emotion grid in the same place as in a conversation line's settings. */}
            <section className="min-h-0 flex items-center justify-center p-4 rounded-lg border border-solid border-interaction-primary-washed bg-interaction-primary-washed overflow-y-auto pit-scrollbar">
                <EmotionBubbleHero emotionBubble={emotionBubble} propsInTake={propsInTake} />
            </section>

            <PiSettingsList className="min-h-0 max-h-full overflow-y-auto pit-scrollbar">
                <PiSettingsRow label={String(t("bubble_ui.speaker"))}>
                    <PiActorPropChipSelect
                        aria-label={String(t("bubble_ui.speaker"))}
                        value={emotionBubble.npcId}
                        onChange={props.onAnchorChange}
                        availableProps={propsInTake}
                    />
                </PiSettingsRow>
                <PiSettingsRow label={String(t("bubble_ui.talks_to"))} disabled={isPropAnchor}>
                    <PiActorChipSelect
                        aria-label={String(t("bubble_ui.talks_to"))}
                        value={isPropAnchor ? "" : emotionBubble.lookAt}
                        onChange={(actorId) => updateInteraction("lookAt", actorId)}
                        excludeActors={[emotionBubble.npcId]}
                        allowKeepCurrent
                        allowNoActor
                        // A prop cannot look at anyone.
                        disabled={isPropAnchor}
                    />
                </PiSettingsRow>
                {/* How the speaker looks at the listener; only meaningful when there is one. */}
                <PiSettingsRow
                    label={String(t("look_mode.label"))}
                    disabled={isPropAnchor || !hasLookatTarget(emotionBubble.lookAt)}
                >
                    <PiLookModeChipSelect
                        value={emotionBubble.finalIK}
                        onChange={(finalIK) => updateInteraction("finalIK", finalIK)}
                        disabled={isPropAnchor || !hasLookatTarget(emotionBubble.lookAt)}
                    />
                </PiSettingsRow>

                <PiSettingsGap />
                <PiSettingsRow
                    label={String(t("bubble_ui.animation"))}
                    below={
                        <PiAnimationPicker
                            disabled={isPropAnchor}
                            selectedAnimation={emotionBubble.animation}
                            onChange={(animation) => updateInteraction("animation", animation)}
                        />
                    }
                />
                <PiSettingsRow
                    label={String(t("conversation_ui.emotion"))}
                    below={
                        <PiEmotionGrid
                            value={emotionBubble.messageBubbleStyle}
                            onChange={(emotion) => updateInteraction("messageBubbleStyle", emotion)}
                        />
                    }
                >
                    <span className="text-[13px] font-semibold text-interaction-primary" data-testid="current-emotion-label">
                        {currentEmotion?.displayName ?? emotionBubble.messageBubbleStyle}
                    </span>
                </PiSettingsRow>
            </PiSettingsList>
        </div>
    );
}

/**
 * The emoji in a round speech bubble with the speaker under it (Design Studio
 * `screens/lookat-rework/e3-emotion.html` › Emotion Box).
 */
function EmotionBubbleHero(props: { emotionBubble: IEmotionBubble; propsInTake: Prop[] }) {
    const { emotionBubble, propsInTake } = props;
    const actors = useSelector(selectActors);
    const { getPropDTO } = usePropHelper();
    const actor = actors.find((a) => a.id === emotionBubble.npcId);
    const prop = actor ? undefined : propsInTake.find((p) => p.id === emotionBubble.npcId);
    const emotion = emotionBubbleStylesMapped.find((e) => e.name === emotionBubble.messageBubbleStyle);

    return (
        <div className="flex flex-col items-center gap-4" data-testid="emotion-hero">
            <div className="relative grid place-items-center w-[148px] h-[148px] p-6 box-border rounded-full bg-interaction-background shadow-[0_6px_18px_rgba(21,52,88,0.08)]">
                {emotion && <img className="w-full h-full object-contain" src={emotion.path} alt={emotion.displayName} />}
                <span
                    aria-hidden="true"
                    className="absolute left-1/2 -translate-x-1/2 w-0 h-0 border-solid border-x-[14px] border-t-[14px] border-b-0 border-x-transparent"
                    style={{ bottom: -8, borderTopColor: "var(--pi-background)" }}
                />
            </div>
            <div className="flex items-center gap-2">
                {prop ? (
                    <PropAvatar size="36" src={getPropDTO(prop.propId)?.propContent?.downloadUri} />
                ) : (
                    <ActorAvatar actor={actor} />
                )}
                <span className="text-[20px] font-medium text-interaction-secondary">
                    {actor?.workingName ?? prop?.name ?? ""}
                </span>
            </div>
        </div>
    );
}

function DisabledLookAtField(props: { label: string }) {
    return (
        <div className="w-full h-[98px] opacity-60">
            {props.label && (
                <div className="text-base font-bold text-interaction-primary h-[30px]">
                    {props.label}
                </div>
            )}
            <div className="flex p-4 w-full h-[68px] align-middle border-b-2 border-solid bg-interaction-softBackground border-interaction-primary items-center cursor-not-allowed">
                <div className="w-full font-medium text-interaction-primary text-interaction text-interaction-base">
                    No actor
                </div>
            </div>
        </div>
    );
}

function SettingsSection(props: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-2 min-w-0 min-h-0">
            {props.label && (
                <div className="text-base font-bold text-interaction-primary">
                    {props.label}
                </div>
            )}
            {props.children}
        </div>
    );
}
